'use client';

import { CalendarIcon, ChevronLeft, ChevronRight, X } from 'lucide-react';
import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    AriaDatePickerProps,
    AriaTimeFieldProps,
    CalendarProps,
    DateValue,
    TimeValue,
    useButton,
    useCalendar,
    useCalendarCell,
    useCalendarGrid,
    useDateField,
    useDatePicker,
    useDateSegment,
    useLocale,
    useTimeField,
} from 'react-aria';
import {
    CalendarState,
    DateFieldState,
    DatePickerState,
    DatePickerStateOptions,
    TimeFieldStateOptions,
    useCalendarState,
    useDateFieldState,
    useDatePickerState,
    useTimeFieldState,
} from 'react-stately';
import { cn } from "../../utils.ts";

import { Button } from './button.tsx';
import { Popover, PopoverContent, PopoverTrigger } from './popover.tsx';
import {
    CalendarDate,
    createCalendar,
    getLocalTimeZone,
    getWeeksInMonth,
    parseDateTime,
    fromDate,
    toCalendarDateTime,
    isToday as _isToday,
    toCalendarDate,
} from '@internationalized/date';
import { DateSegment as IDateSegment } from '@react-stately/datepicker';

function Calendar(props: CalendarProps<DateValue>) {
    const prevButtonRef = React.useRef<HTMLButtonElement | null>(null);
    const nextButtonRef = React.useRef<HTMLButtonElement | null>(null);

    const { locale } = useLocale();
    const state = useCalendarState({
        ...props,
        locale,
        createCalendar,
    });
    const {
        calendarProps,
        prevButtonProps: _prevButtonProps,
        nextButtonProps: _nextButtonProps,
        title,
    } = useCalendar(props, state);
    const { buttonProps: prevButtonProps } = useButton(_prevButtonProps, prevButtonRef);
    const { buttonProps: nextButtonProps } = useButton(_nextButtonProps, nextButtonRef);

    return (
        <div {...calendarProps} className="space-y-4" aria-label="Calendar">
            <div className="relative flex items-center justify-center pt-1">
                <Button
                    {...prevButtonProps}
                    ref={prevButtonRef}
                    variant="outline"
                    aria-label="Previous month"
                    className={cn('absolute border-transparent hover:border-border left-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100')}
                >
                    <ChevronLeft className="h-5 w-5 text-foreground" />
                </Button>
                <div className="text-muted-foreground font-500 text-md" aria-label="Current month and year">
                    {title}
                </div>
                <Button
                    {...nextButtonProps}
                    ref={nextButtonRef}
                    variant="outline"
                    aria-label="Next month"
                    className={cn('absolute border-transparent hover:border-border right-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100')}
                >
                    <ChevronRight className="h-5 w-5 text-foreground" />
                </Button>
            </div>
            <Button
                variant="outline"
                aria-label="Select today's date"
                onClick={() => {
                    const today = new Date();
                    state.selectDate(new CalendarDate(today.getFullYear(), today.getMonth() + 1, today.getDate()))
                    state.setFocusedDate(new CalendarDate(today.getFullYear(), today.getMonth() + 1, today.getDate()))
                }}>
                Today
            </Button>
            <CalendarGrid state={state} />
        </div>
    );
}

interface CalendarGridProps {
    state: CalendarState;
}

function CalendarGrid({ state, ...props }: CalendarGridProps) {
    const { locale } = useLocale();
    const { gridProps, headerProps, weekDays } = useCalendarGrid(props, state);

    const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);

    return (
        <table {...gridProps} className={cn(gridProps.className, 'w-full border-collapse space-y-1')} aria-label="Month calendar">
            <thead {...headerProps} className="w-full">
                <tr className="flex">
                    {weekDays.map((day, index) => (
                        <th
                            className="w-9 rounded-md text-[0.8rem] font-500 text-muted-foreground"
                            key={index}
                            scope="col"
                            aria-label={day}
                        >
                            {day}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {[...new Array(weeksInMonth).keys()].map((weekIndex) => (
                    <tr className="mt-2 flex w-full" key={weekIndex}>
                        {state
                            .getDatesInWeek(weekIndex)
                            .map((date, i) =>
                                date ? <CalendarCell key={i} state={state} date={date} /> : <td key={i} />
                            )}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

interface CalendarCellProps {
    state: CalendarState;
    date: CalendarDate;
}

function CalendarCell({ state, date }: CalendarCellProps) {
    const ref = React.useRef<HTMLButtonElement | null>(null);
    const { cellProps, buttonProps, isSelected, isOutsideVisibleRange, isDisabled, formattedDate } =
        useCalendarCell({ date }, state, ref);

    const isToday = useMemo(() => {
        const timezone = getLocalTimeZone();
        return _isToday(date, timezone);
    }, [date]);

    return (
        <td
            {...cellProps}
            className={cn(
                cellProps.className,
                'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md',
            )}
        >
            <Button
                {...buttonProps}
                type="button"
                variant="ghost"
                ref={ref}
                aria-label={`${formattedDate}${isToday ? ', today' : ''}${isSelected ? ', selected' : ''}`}
                className={cn(
                    buttonProps.className,
                    'h-9 w-9 rounded-full p-1 font-500',
                    isToday && 'text-primary',
                    isSelected &&
                    'bg-primary text-primary-foreground hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
                    isOutsideVisibleRange && 'text-muted-foreground opacity-50',
                    isDisabled && 'text-muted-foreground opacity-50',
                )}
            >
                {formattedDate}
            </Button>
        </td>
    );
}

interface DateSegmentProps {
    segment: IDateSegment;
    state: DateFieldState;
}

function DateSegment({ segment, state }: DateSegmentProps) {
    const ref = useRef(null);

    const {
        segmentProps: { ...segmentProps },
    } = useDateSegment(segment, state, ref);

    return (
        <div
            {...segmentProps}
            ref={ref}
            aria-label={`${segment.type} segment, ${segment.text}`}
            className={cn(
                'focus:rounded-[2px] focus:bg-accent focus:text-accent-foreground focus:outline-none',
                segment.type !== 'literal' && 'px-[1px]',
                segment.isPlaceholder && 'text-muted-foreground',
            )}
        >
            {segment.text}
        </div>
    );
}

function DateField(props: AriaDatePickerProps<DateValue>) {
    const ref = useRef<HTMLDivElement | null>(null);

    const { locale } = useLocale();
    const state = useDateFieldState({
        ...props,
        locale,
        createCalendar,
    });
    const { fieldProps } = useDateField(props, state, ref);

    // Usa el aria-label de props, no solo el de fieldProps
    const ariaLabel = props['aria-label'] || "Select date";

    return (
        <div
            {...fieldProps}
            ref={ref}
            aria-label={ariaLabel}
            className={cn(
                'inline-flex h-10 flex-1 text-foreground items-center rounded-l-md bg-transparent px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                props.isDisabled && 'cursor-not-allowed opacity-50',
            )}
        >
            {state.segments.map((segment, i) => (
                <DateSegment key={i} segment={segment} state={state} />
            ))}
            {state.isInvalid && <span aria-hidden="true">🚫</span>}
        </div>
    );
}

function TimeField(props: AriaTimeFieldProps<TimeValue>) {
    const ref = useRef<HTMLDivElement | null>(null);

    const { locale } = useLocale();
    const state = useTimeFieldState({
        ...props,
        locale,
    });
    const {
        fieldProps: { ...fieldProps },
        labelProps,
    } = useTimeField(props, state, ref);

    // Usa el aria-label de props, no solo el de fieldProps
    const ariaLabel = props['aria-label'] || "Select time";

    return (
        <div
            {...fieldProps}
            ref={ref}
            aria-label={ariaLabel}
            className={cn(
                'inline-flex h-10 w-full flex-1 rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                props.isDisabled && 'cursor-not-allowed opacity-50',
            )}
        >
            {state.segments.map((segment, i) => (
                <DateSegment key={i} segment={segment} state={state} />
            ))}
        </div>
    );
}

const TimePicker = React.forwardRef<
    HTMLDivElement,
    Omit<TimeFieldStateOptions<TimeValue>, 'locale'>
>((props, forwardedRef) => {
    return <TimeField {...props} />;
});

TimePicker.displayName = 'TimePicker';

export type DateTimePickerRef = {
    divRef: HTMLDivElement | null;
    buttonRef: HTMLButtonElement | null;
    contentRef: HTMLDivElement | null;
    jsDate: Date | null;
    state: DatePickerState;
};

const DateTimePicker = React.forwardRef<
    DateTimePickerRef,
    DatePickerStateOptions<DateValue> & {
        jsDate?: Date | null;
        onJsDateChange?: (date: Date) => void;
        showClearButton?: boolean;
    }
>(({ jsDate, onJsDateChange, showClearButton = true, ...props }, ref) => {
    const divRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [jsDatetime, setJsDatetime] = useState(jsDate || null);

    const state = useDatePickerState(props);

    useImperativeHandle(ref, () => ({
        divRef: divRef.current,
        buttonRef: buttonRef.current,
        contentRef: contentRef.current,
        jsDate: jsDatetime,
        state,
    }));
    const {
        groupProps,
        fieldProps,
        buttonProps: _buttonProps,
        dialogProps,
        calendarProps,
    } = useDatePicker(props, state, divRef);
    const { buttonProps } = useButton(_buttonProps, buttonRef);

    const currentValue = useCallback(() => {
        if (!jsDatetime) {
            return null;
        }

        const parsed = fromDate(jsDatetime, getLocalTimeZone());

        if (state.hasTime) {
            return toCalendarDateTime(parsed);
        }

        return toCalendarDate(parsed);
    }, [jsDatetime, state.hasTime]);

    useEffect(() => {
        if (state.value) {
            const date = parseDateTime(state.value.toString()).toDate(getLocalTimeZone());
            setJsDatetime(date);
            onJsDateChange?.(date);
        }
    }, [state.value, onJsDateChange]);

    return (
        <div
            {...groupProps}
            ref={divRef}
            aria-label="Date and time picker"
            className={cn(
                groupProps.className,
                'flex items-center rounded-md border border-input ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
            )}
        >
            <Popover open={props.isOpen} onOpenChange={props.onOpenChange}>
                <PopoverTrigger asChild>
                    <Button
                        {...buttonProps}
                        variant="ghost"
                        aria-label="Open calendar"
                        className="border-r border-input px-5"
                        disabled={props.isDisabled}
                        onClick={() => {
                            state.setOpen(true);
                        }}
                    >
                        <CalendarIcon className="h-5 w-5" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent ref={contentRef} className="w-full">
                    <div {...dialogProps} className="space-y-3">
                        <>
                            <Calendar {...calendarProps} />
                        </>
                        {state.hasTime && (
                            <TimeField
                                value={state.timeValue}
                                aria-label="Select time"
                                onChange={state.setTimeValue}
                            />
                        )}
                    </div>
                </PopoverContent>
            </Popover>
            <DateField
                {...fieldProps}
                value={currentValue()}
                aria-label={fieldProps['aria-label'] || "Select date"}
            />
            {showClearButton && (
                <div className="-ml-2 mr-2 h-5 w-5">
                    <X
                        className={cn(
                            'h-5 w-5 cursor-pointer text-muted-foreground/60',
                            !jsDatetime && 'hidden'
                        )}
                        aria-label="Clear selected date"
                        onClick={() => {
                            setJsDatetime(null);
                            onJsDateChange?.(null);
                        }}
                    />
                </div>
            )}
        </div>
    );
});

DateTimePicker.displayName = 'DateTimePicker';

export { DateTimePicker, TimePicker };